import React from 'react';

import {
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import useCreateWorkoutMovement from 'hooks/workoutMovement/useCreateWorkoutMovement';
import useUpdateWorkoutMovement from 'hooks/workoutMovement/useUpdateWorkoutMovement';
import useWorkoutMovement from 'hooks/workoutMovement/useWorkoutMovement';
import { WorkoutMovementWrite } from 'types';
import { MovementTypeEnum, SCORE_TYPE_OPTIONS, ScoreTypeEnum } from 'types/enums';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

type DefaultScoreType = WorkoutMovementWrite['defaultScoreType'];

interface FormValues {
  name: string;
  description: string;
  defaultScoreType: ScoreTypeEnum | null;
  workoutMovementId: string;
}

const normalizeDefaultScoreType = (value: DefaultScoreType): ScoreTypeEnum | null => {
  if (!value) return null;
  return value as ScoreTypeEnum;
};

interface IModalMovementFormProps {
  opened: boolean;
  closeHandler: () => void;
  workoutMovementId?: string;
  type: 'create' | 'edit';
  permission?: boolean;
}

const ModalMovementForm = ({
  opened,
  closeHandler,
  workoutMovementId,
  type,
  permission = true,
}: IModalMovementFormProps): JSX.Element => {
  const createMutation = useCreateWorkoutMovement();
  const updateMutation = useUpdateWorkoutMovement();

  const { data: movementData, isLoading: isLoadingMovement } = useWorkoutMovement(workoutMovementId);

  const movement = movementData?.data;

  const form = useForm<FormValues>({
    initialValues: {
      name: '',
      description: '',
      defaultScoreType: null,
      workoutMovementId: uuidv4(),
    },
    validate: {
      name: value => {
        if (!value || value.trim() === '') return 'Movement name is required';
        if (value.length > 255) return 'Movement name must be 255 characters or less';
        return null;
      },
    },
  });

  const { successNotification, errorNotification } = notificationHandler();

  useShallowEffect(() => {
    if (type === 'edit' && movement) {
      form.setValues({
        name: movement.name,
        description: movement.description ?? '',
        defaultScoreType: normalizeDefaultScoreType(movement.defaultScoreType),
        workoutMovementId: movement.workoutMovementId,
      });
    } else if (type === 'create') {
      form.reset();
      form.setFieldValue('workoutMovementId', uuidv4());
    }
  }, [type, movement, opened]);

  const handleCloseAndReset = (): void => {
    form.reset();
    closeHandler();
  };

  const handleSubmit = (values: FormValues): void => {
    const payload: WorkoutMovementWrite = {
      workoutMovementId: values.workoutMovementId,
      name: values.name.trim(),
      description: values.description.trim() || null,
      defaultScoreType: values.defaultScoreType,
      movementType: MovementTypeEnum.MOVEMENT,
    };

    if (type === 'create') {
      createMutation.mutate(payload, {
        onSuccess: () => {
          successNotification('Movement created', `${values.name} has been created successfully.`);
          handleCloseAndReset();
        },
        onError: error => {
          const errorMessage = error.response?.data?.message || 'Failed to create movement';
          errorNotification('Creation failed', errorMessage);
        },
      });
    } else {
      updateMutation.mutate(
        {
          workoutMovementId: workoutMovementId!,
          data: {
            workoutMovementId: workoutMovementId!,
            name: payload.name,
            description: payload.description,
            defaultScoreType: payload.defaultScoreType,
            movementType: MovementTypeEnum.MOVEMENT,
          },
        },
        {
          onSuccess: () => {
            successNotification('Movement updated', `${values.name} has been updated successfully.`);
            handleCloseAndReset();
          },
          onError: error => {
            const errorMessage = error.response?.data?.message || 'Failed to update movement';
            errorNotification('Update failed', errorMessage);
          },
        },
      );
    }
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={4}>{type === 'create' ? 'Create Movement' : 'Edit Movement'}</Title>}
      size='md'
    >
      <LoadingOverlay visible={isLoadingMovement} />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='md'>
          <TextInput
            label='Name'
            placeholder='Enter movement name'
            required
            disabled={!permission}
            {...form.getInputProps('name')}
            data-cy='movementNameInput'
          />
          <Textarea
            label='Description'
            placeholder='Enter movement description (optional)'
            disabled={!permission}
            minRows={3}
            {...form.getInputProps('description')}
            data-cy='movementDescriptionInput'
          />
          <Select
            label='Default Score Type'
            placeholder='Optional — select a score type'
            data={SCORE_TYPE_OPTIONS}
            disabled={!permission}
            clearable
            {...form.getInputProps('defaultScoreType')}
            data-cy='movementDefaultScoreTypeInput'
          />

          <Group position='right' mt='md'>
            <Button
              variant='subtle'
              onClick={handleCloseAndReset}
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              type='submit'
              loading={createMutation.isLoading || updateMutation.isLoading}
              disabled={!permission}
            >
              {type === 'create' ? 'Create' : 'Save Changes'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default ModalMovementForm;
